/**
 * variant-picker-size  –  Chip-based size selector custom element
 * ================================================================
 * All size-switch updates (price, URL, button) are handled client-side
 * from a pre-rendered variant map — no section fetch required.
 *
 * The pub/sub variantChange subscription is kept solely to react when
 * the *color* picker triggers a server fetch (chip availability may
 * change across colors).
 */

if (!customElements.get('variant-picker-size')) {
  class VariantPickerSize extends HTMLElement {
    constructor() {
      super();
      this.chips = [];
      this.selectedChip = null;
      this.variantMap = {};
      this.priceLabels = {};
    }

    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.parentCollectionHandle = this.dataset.parentCollectionHandle || 'all';
      this.productColor = this.dataset.productColor || '';
      this.productUrl = this.dataset.productUrl || '';

      // Parse enriched variant data
      const mapEl = this.querySelector('[data-variant-map]');
      const labelsEl = this.querySelector('[data-price-labels]');
      if (mapEl) {
        try { this.variantMap = JSON.parse(mapEl.textContent); } catch (e) { /* */ }
      }
      if (labelsEl) {
        try { this.priceLabels = JSON.parse(labelsEl.textContent); } catch (e) { /* */ }
      }

      this.selectedValueDisplay = this.querySelector('[data-selected-value]');
      this.lowStockEl = this.querySelector('[data-low-stock-message]');
      this.seeSimilarEl = this.querySelector('[data-see-similar]');
      this.notifyContainer = this.querySelector('[data-notify-container]');
      this.disableNotifyMe = this.dataset.disableNotifyMe === 'true';
      this._segunoLoaded = false;

      this.chips = Array.from(this.querySelectorAll('.variant-picker-size__chip[role="radio"]'));

      // Bind chip clicks
      this.chips.forEach(chip => {
        chip.addEventListener('click', this.onChipClick.bind(this));
      });

      // EU/UK size unit toggle
      // Server picks the initial unit (UK for UK market, EU otherwise) and
      // renders chips + toggle state to match. A saved localStorage preference
      // wins over the server default so an explicit choice always sticks.
      const defaultUnit = this.dataset.defaultUnit === 'uk' ? 'uk' : 'eu';
      this.sizeUnit = defaultUnit;
      this.unitToggle = this.querySelector('[data-size-unit-toggle]');
      if (this.unitToggle) {
        let savedUnit = null;
        try {
          const saved = localStorage.getItem('variant-picker-size__unit');
          if (saved === 'uk' || saved === 'eu') savedUnit = saved;
        } catch (e) { /* localStorage unavailable */ }
        this.unitToggle.querySelectorAll('[data-unit]').forEach(btn => {
          btn.addEventListener('click', this.onUnitToggle.bind(this));
        });
        // Only reapply if the saved preference diverges from the server default —
        // skips a needless DOM rewrite (and any brief flash) in the common case.
        if (savedUnit && savedUnit !== defaultUnit) {
          this.applySizeUnit(savedUnit);
        }
      }

      // Keyboard navigation (arrow keys within radiogroup)
      const grid = this.querySelector('[role="radiogroup"]');
      if (grid) {
        grid.addEventListener('keydown', this.onKeyDown.bind(this));
      }

      // Subscribe to variant changes from ProductInfo (e.g. when color changes)
      this._variantChangeUnsub = subscribe(
        PUB_SUB_EVENTS.variantChange,
        this.onVariantChange.bind(this)
      );

      // Gate ATC on load — no size selected initially.
      // If product is fully OOS, the button is already server-rendered
      // as disabled with "Sold out" text via Liquid.
      this.gateAddToCart();

      // Prefetch Seguno script after page load so it's cached when an OOS chip is clicked.
      // Uses <link rel="prefetch"> to download without executing.
      if (this.notifyContainer && !this.disableNotifyMe) {
        const prefetch = () => {
          const link = document.createElement('link');
          link.rel = 'prefetch';
          link.href = 'https://cdn.seguno.com/back-in-stock/back_in_stock.js';
          link.as = 'script';
          document.head.appendChild(link);
        };
        if (document.readyState === 'complete') {
          prefetch();
        } else {
          window.addEventListener('load', prefetch, { once: true });
        }
      }
    }

    disconnectedCallback() {
      if (this._variantChangeUnsub) this._variantChangeUnsub();
    }

    /* ------------------------------------------------------------------ */
    /*  Chip click handler                                                 */
    /* ------------------------------------------------------------------ */
    onChipClick(e) {
      const chip = e.currentTarget;

      // Deselect all chips
      this.chips.forEach(c => c.setAttribute('aria-checked', 'false'));

      // Select this chip
      chip.setAttribute('aria-checked', 'true');
      chip.focus();
      this.selectedChip = chip;

      const sizeLabel = chip.dataset.sizeLabel;
      const isAvailable = chip.dataset.available === 'true';
      const variantId = chip.dataset.variantId;
      const isLowStock = chip.dataset.lowStock === 'true';

      // Update selected value display in the currently selected unit
      if (this.selectedValueDisplay) {
        this.selectedValueDisplay.textContent = this.getDisplayLabel(chip);
      }

      // Toggle low stock message (ACT-5)
      this.toggleLowStock(isLowStock);

      // Toggle see-similar link (ACT-7)
      this.toggleSeeSimilar(isAvailable, sizeLabel);

      // Toggle notify-me (ACT-6)
      this.toggleNotifyMe(isAvailable, variantId);

      // Update ATC button state (ACT-3)
      this.updateAddToCart(isAvailable, variantId);

      // Update price from local variant data — no fetch
      this.updatePrice(variantId);

      // Update browser URL
      this.updateURL(variantId);
    }

    /* ------------------------------------------------------------------ */
    /*  Size unit toggle (EU / UK)                                         */
    /* ------------------------------------------------------------------ */
    onUnitToggle(e) {
      const unit = e.currentTarget.dataset.unit;
      if (unit !== 'eu' && unit !== 'uk') return;
      if (unit === this.sizeUnit) return;
      this.applySizeUnit(unit);
      try { localStorage.setItem('variant-picker-size__unit', unit); } catch (err) { /* */ }
    }

    applySizeUnit(unit) {
      this.sizeUnit = unit;

      // Toggle button state
      if (this.unitToggle) {
        this.unitToggle.querySelectorAll('[data-unit]').forEach(btn => {
          const isActive = btn.dataset.unit === unit;
          btn.classList.toggle('is-active', isActive);
          btn.setAttribute('aria-checked', isActive ? 'true' : 'false');
        });
      }

      // Rewrite every chip's visible label
      this.chips.forEach(chip => {
        const labelEl = chip.querySelector('.variant-picker-size__chip-label');
        if (labelEl) labelEl.textContent = this.getDisplayLabel(chip);
      });

      // Rewrite the selected-value display if a chip is selected
      if (this.selectedChip && this.selectedValueDisplay) {
        this.selectedValueDisplay.textContent = this.getDisplayLabel(this.selectedChip);
      }
    }

    // Returns the label to display for a chip in the current unit.
    // Falls back to the EU label when the chip has no UK value.
    getDisplayLabel(chip) {
      const eu = chip.dataset.sizeLabel || '';
      if (this.sizeUnit === 'uk') {
        const uk = chip.dataset.sizeUk || '';
        return uk !== '' ? uk : eu;
      }
      return eu;
    }

    /* ------------------------------------------------------------------ */
    /*  Keyboard navigation (ACT-9)                                        */
    /* ------------------------------------------------------------------ */
    onKeyDown(e) {
      const { key } = e;
      if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(key)) return;

      e.preventDefault();
      const currentIndex = this.chips.indexOf(document.activeElement);
      if (currentIndex === -1) return;

      let nextIndex;
      if (key === 'ArrowRight' || key === 'ArrowDown') {
        nextIndex = (currentIndex + 1) % this.chips.length;
      } else {
        nextIndex = (currentIndex - 1 + this.chips.length) % this.chips.length;
      }

      // Move focus and select
      this.chips[nextIndex].click();
    }

    /* ------------------------------------------------------------------ */
    /*  Low stock (ACT-5): server computes the ≤ 3 rule into a boolean   */
    /* ------------------------------------------------------------------ */
    toggleLowStock(isLowStock) {
      if (!this.lowStockEl) return;
      this.lowStockEl.classList.toggle('is-visible', isLowStock);
    }

    /* ------------------------------------------------------------------ */
    /*  See Similar link (ACT-7)                                           */
    /* ------------------------------------------------------------------ */
    toggleSeeSimilar(isAvailable, sizeLabel) {
      if (!this.seeSimilarEl) return;
      // Hide "See similar" when notify-me is active — Seguno form takes priority
      const notifyActive = !isAvailable && !this.disableNotifyMe && this.notifyContainer;
      if (!isAvailable && !notifyActive) {
        let href = `/collections/${this.parentCollectionHandle}?filter.v.option.shoe+size=${encodeURIComponent(sizeLabel)}`;
        if (this.productColor) {
          href += `&filter.v.t.shopify.color-pattern=${encodeURIComponent(this.productColor)}`;
        }
        href += '&sort_by=title-ascending';
        this.seeSimilarEl.setAttribute('href', href);
        this.seeSimilarEl.classList.add('is-visible');
      } else {
        this.seeSimilarEl.classList.remove('is-visible');
      }
    }

    /* ------------------------------------------------------------------ */
    /*  Notify Me (ACT-6)                                                  */
    /* ------------------------------------------------------------------ */
    toggleNotifyMe(isAvailable, variantId) {
      if (!this.notifyContainer || this.disableNotifyMe) return;
      const show = !isAvailable;
      this.notifyContainer.classList.toggle('is-visible', show);

      if (show) this.renderSegunoForm(variantId);
    }

    renderSegunoForm(variantId) {
      if (!this._segunoLoaded) {
        // First time: load the script. Seguno reads the JSON config
        // element and renders the form automatically.
        this._segunoLoaded = true;
        const s = document.createElement('script');
        s.src = 'https://cdn.seguno.com/back-in-stock/back_in_stock.js';
        s.async = true;
        document.body.appendChild(s);
        return;
      }

      // Seguno uses shadow DOM — access internals via shadowRoot
      const bisEl = this.notifyContainer.querySelector('seguno-back-in-stock');
      if (!bisEl || !bisEl.shadowRoot) return;

      const shadow = bisEl.shadowRoot;

      // Update the variant Seguno will track
      const variantInput = shadow.querySelector('#variant-id');
      if (variantInput) variantInput.value = variantId;

      // If the form was submitted (success visible), reset it
      const successEl = shadow.querySelector('#success');
      const formEl = shadow.querySelector('#form');
      if (successEl && successEl.style.display === 'block') {
        successEl.style.display = 'none';
        if (formEl) {
          formEl.style.display = '';
          const emailInput = formEl.querySelector('#email');
          if (emailInput) emailInput.value = '';
        }
      }
    }

    /* ------------------------------------------------------------------ */
    /*  ATC button gating (ACT-3)                                          */
    /* ------------------------------------------------------------------ */
    gateAddToCart() {
      document.body.removeAttribute('data-variant-selected');
    }

    updateAddToCart(isAvailable, variantId) {
      document.body.setAttribute('data-variant-selected', '');

      const productForm = this.getProductForm();
      if (!productForm) return;

      const submitBtn = productForm.querySelector('[type="submit"]');
      const mainText = submitBtn?.querySelector('.main-product-buy-btn-text');
      const selectSizeText = submitBtn?.querySelector('.select-the-size-first-text');

      // Hide "Select a size" immediately
      if (selectSizeText) selectSizeText.style.display = 'none';
      if (mainText) mainText.style.display = 'block';

      // Update the hidden variant ID input
      const idInput = productForm.querySelector('input[name="id"]');
      if (idInput) {
        idInput.value = variantId;
        if (isAvailable) {
          idInput.removeAttribute('disabled');
        } else {
          idInput.setAttribute('disabled', 'disabled');
        }
      }

      const showNotify = !isAvailable && !this.disableNotifyMe && this.notifyContainer;

      if (isAvailable) {
        productForm.toggleSubmitButton(false, window.variantStrings?.addToCart || '');
        if (submitBtn) submitBtn.style.display = '';
      } else if (showNotify) {
        // OOS with notify-me: hide ATC, Seguno form takes its place
        if (submitBtn) submitBtn.style.display = 'none';
      } else {
        // OOS without notify-me: show "Sold out" disabled
        if (submitBtn) submitBtn.style.display = '';
        productForm.toggleSubmitButton(true, window.variantStrings?.soldOut || '');
      }
    }

    getProductForm() {
      const productInfo = this.closest('product-info');
      if (productInfo) return productInfo.querySelector('product-form');
      return document.querySelector(`product-form[data-section-id="${this.sectionId}"]`);
    }

    /* ------------------------------------------------------------------ */
    /*  Price update from local variant data                               */
    /* ------------------------------------------------------------------ */
    updatePrice(variantId) {
      const v = this.variantMap[variantId];
      if (!v) return;

      const fmt = this.priceLabels.moneyFormat || window.theme?.settings?.money_format;
      if (!fmt || typeof formatPrice !== 'function') return;

      const price = v.price / 100;
      const compareAt = v.compare_at_price ? v.compare_at_price / 100 : null;
      const priceFormatted = formatPrice(fmt, price);
      const compareFormatted = compareAt ? formatPrice(fmt, compareAt) : null;

      // Update the price container
      const priceContainer = document.querySelector(`#price-${this.sectionId} .price__container`);
      if (!priceContainer) return;

      // Update price classes on parent .price element
      const priceEl = priceContainer.closest('.price');
      if (priceEl) {
        priceEl.classList.toggle('price--on-sale', compareAt != null && compareAt > price);
        priceEl.classList.toggle('price--sold-out', !v.available);
      }

      // Build price HTML
      let html = '';
      if (compareAt && compareAt > price) {
        const saleLbl = this.priceLabels.sale || '';
        const regLbl = this.priceLabels.regular || '';
        html = `
          <div class="price__sale">
            <div class="price__sale-inner">
              <span class="visually-hidden">${regLbl}</span>
              <s>${compareFormatted}</s>
              <ins>
                <span class="visually-hidden">${saleLbl}</span>
                <ins>${priceFormatted}</ins>
              </ins>
            </div>
          </div>`;
      } else {
        const regLbl = this.priceLabels.regular || '';
        html = `
          <div class="price__regular">
            <span class="visually-hidden">${regLbl}</span>
            <span>${priceFormatted}</span>
          </div>`;
      }
      priceContainer.innerHTML = html;

      // Update best-price container
      const bestPriceContainer = document.querySelector(`#price-${this.sectionId} .best-price-container`);
      if (bestPriceContainer) {
        this.updateBestPrice(bestPriceContainer, v, price, compareAt);
      }
    }

    updateBestPrice(container, v, currentPrice, compareAt) {
      const fmt = this.priceLabels.moneyFormat || window.theme?.settings?.money_format;
      let html = '';

      if (v.best_price != null && v.was_price != null) {
        const bestPrice = parseFloat(v.best_price);
        const wasPrice = parseFloat(v.was_price);

        // 30-day best price line
        if (this.priceLabels.showBestPrice && compareAt != null && compareAt !== currentPrice) {
          const bestDiff = bestPrice - currentPrice;
          const bestPercent = Math.round((bestDiff / bestPrice) * 100);
          const bestFormatted = formatPrice(fmt, bestPrice);
          html += `
            <div class="best-price-container-item-best-price">
              ${this.priceLabels.bestPrice || ''}
              <span>${bestFormatted} (-${bestPercent}%)</span>
            </div>`;
        }

        // "Was" line
        if (currentPrice < wasPrice) {
          const wasDiff = wasPrice - currentPrice;
          const wasPercent = Math.round((wasDiff / wasPrice) * 100);
          const wasFormatted = formatPrice(fmt, wasPrice);
          html += `
            <div class="best-price-container-item-was">
              ${this.priceLabels.was || ''}
              <span>${wasFormatted} (-${wasPercent}%)</span>
            </div>`;
        }
      }

      container.innerHTML = html;
      container.style.display = html ? '' : 'none';
    }

    /* ------------------------------------------------------------------ */
    /*  URL update                                                         */
    /* ------------------------------------------------------------------ */
    updateURL(variantId) {
      if (!variantId) return;
      const params = new URLSearchParams(window.location.search);
      params.set('variant', variantId);
      const newUrl = `${this.productUrl}?${params.toString()}`;
      window.history.replaceState({}, '', newUrl);
    }

    /* ------------------------------------------------------------------ */
    /*  Handle variant changes from other option pickers (e.g. color)      */
    /*  This only fires when the COLOR picker triggers a section fetch.    */
    /* ------------------------------------------------------------------ */
    onVariantChange({ data }) {
      // Only respond to changes in our section
      if (data.sectionId !== this.sectionId) return;

      const html = data.html;
      if (!html) return;

      // Find the new variant-picker-size in the returned HTML
      const newPicker = html.querySelector(`#VariantPickerSize-${this.sectionId}`);
      if (!newPicker) return;

      // Update the variant map from new server data
      const newMapEl = newPicker.querySelector('[data-variant-map]');
      if (newMapEl) {
        try { this.variantMap = JSON.parse(newMapEl.textContent); } catch (e) { /* */ }
      }

      // Update chip availability states from server-rendered HTML
      const newChips = newPicker.querySelectorAll('.variant-picker-size__chip[role="radio"]');
      newChips.forEach(newChip => {
        const optionValueId = newChip.dataset.optionValueId;
        const existingChip = this.querySelector(
          `.variant-picker-size__chip[data-option-value-id="${optionValueId}"]`
        );
        if (!existingChip) return;

        // Update data attributes
        existingChip.dataset.available = newChip.dataset.available;
        existingChip.dataset.variantId = newChip.dataset.variantId;
        existingChip.dataset.lowStock = newChip.dataset.lowStock;
        existingChip.dataset.productUrl = newChip.dataset.productUrl;
        if (newChip.dataset.sizeUk !== undefined) {
          existingChip.dataset.sizeUk = newChip.dataset.sizeUk;
        }

        // Update OOS class
        const isAvailable = newChip.dataset.available === 'true';
        existingChip.classList.toggle('variant-picker-size__chip--oos', !isAvailable);
        if (!isAvailable) {
          existingChip.setAttribute('aria-disabled', 'true');
        } else {
          existingChip.removeAttribute('aria-disabled');
        }
      });

      // Re-apply the current size unit so the visible labels match the
      // freshly-synced dataset (UK sizes may differ across colors).
      if (this.unitToggle) this.applySizeUnit(this.sizeUnit);

      // If a chip was selected, refresh its secondary UI (low stock, etc.)
      if (this.selectedChip) {
        const selId = this.selectedChip.dataset.optionValueId;
        const refreshed = this.querySelector(
          `.variant-picker-size__chip[data-option-value-id="${selId}"]`
        );
        if (refreshed) {
          const isAvailable = refreshed.dataset.available === 'true';
          this.toggleLowStock(refreshed.dataset.lowStock === 'true');
          this.toggleSeeSimilar(isAvailable, refreshed.dataset.sizeLabel);
          this.toggleNotifyMe(isAvailable, refreshed.dataset.variantId);
          this.updateAddToCart(isAvailable, refreshed.dataset.variantId);
          this.updatePrice(refreshed.dataset.variantId);
        }
      }
    }
  }

  customElements.define('variant-picker-size', VariantPickerSize);
}
